import { useEffect, useRef, useState, useCallback } from 'react';
import { Form, Select, DatePicker, Input, Dropdown, Menu } from 'antd';
import { useIntl } from 'react-intl';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';

import {
  FilterOutlined,
  RightOutlined,
  CloseOutlined
} from '@ant-design/icons';

import {
  FORMAT_DATE_TO_UTC,
  cleanEmptyString,
  trimObjectStrings
} from 'utils/helpers';
import { getUserWarehouseScope } from 'common/scopes';
import { formatOptions } from 'utils';
import { getHubs } from 'services/hubs';
import { ROUTES_SEARCH_OPTIONS } from 'constants/route';
import { getAllHubStars } from 'services/routes-form';
import { DATE_FORMAT } from 'constants/sorting-facility';

import BRButton from 'components/BRButton/BRButton';
import { notify } from 'components/Notify/Notify';

import { ReactComponent as Filter } from 'assets/bosta-icons/Filters.svg';
import { ReactComponent as Down } from 'assets/bosta-icons/Down.svg';

import './RoutesSearch.less';

const RoutesSearch = ({
  handleOnSubmit,
  isLoading,
  searchCleared,
  userWareHouse,
  hubFilterDisabled,
  userHubInfo,
  loading,
  setSelectedFilters
}) => {
  const intl = useIntl();
  const { Option } = Select;
  const formRef = useRef();
  const rangePickerRef = useRef(null);

  const [searchType, setSearchType] = useState(ROUTES_SEARCH_OPTIONS[0].value);
  const [hubs, setHubs] = useState([]);
  const [visibleMenuKey, setVisibleMenuKey] = useState(null);
  const [stars, setStars] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedDatesFilters, setSelectedDatesFilters] = useState({
    createdAt: null,
    dispatchedAt: dayjs().format(DATE_FORMAT),
    updatedAt: null
  });
  const [scopedHubs, setScopedHubs] = useState([]);

  const items = [
    {
      key: 'createdAt',
      title: intl.formatMessage({
        id: 'create_route.table_columns.created_at'
      }),
      label: (
        <div className="br-route-search__dates-option">
          {intl.formatMessage({ id: 'create_route.table_columns.created_at' })}
          <RightOutlined />
        </div>
      ),
      onClick: () => setVisibleMenuKey('createdAt')
    },
    {
      key: 'dispatchedAt',
      title: intl.formatMessage({
        id: 'create_route.table_columns.assigned_at'
      }),
      label: (
        <div className="br-route-search__dates-option">
          {intl.formatMessage({ id: 'create_route.table_columns.assigned_at' })}
          <RightOutlined />
        </div>
      ),
      onClick: () => setVisibleMenuKey('dispatchedAt')
    },
    {
      key: 'updatedAt',
      title: intl.formatMessage({
        id: 'create_route.table_columns.updated_at'
      }),
      label: (
        <div className="br-route-search__dates-option">
          {intl.formatMessage({ id: 'create_route.table_columns.updated_at' })}
          <RightOutlined />
        </div>
      ),
      onClick: () => setVisibleMenuKey('updatedAt')
    }
  ];
  const menu = <Menu selectedKeys={[]} items={items} />;

  useEffect(() => {
    fetchHubs();
    if (userWareHouse) {
      getAvailableStars(userWareHouse);
    }
  }, []);

  const getAvailableStars = async (hubId) => {
    if (hubId) {
      try {
        const { message } = await getAllHubStars(hubId);
        setStars(message || []);
      } catch (error) {
        notify(error?.message);
      }
    }
  };

  useEffect(() => {
    if (searchCleared) {
      formRef.current.resetFields();
    }
  }, [searchCleared]);

  const handleHubChange = (hubId) => {
    formRef.current.setFieldsValue({ starName: null });
    getAvailableStars(hubId);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      const pickerDropdown = document.querySelector('.ant-picker-dropdown');
      if (
        rangePickerRef.current &&
        !rangePickerRef.current.contains(event.target) &&
        pickerDropdown &&
        !pickerDropdown.contains(event.target)
      ) {
        setMenuOpen(false);
        setVisibleMenuKey(null);
      }
    }

    if (menuOpen || visibleMenuKey) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen, visibleMenuKey]);

  const fetchHubs = async () => {
    try {
      const { result } = await getHubs();
      setHubs(result);
      const hubs = getUserWarehouseScope(result, true);
      setScopedHubs([userHubInfo, ...(hubs || [])].filter(Boolean));
    } catch (error) {
      notify(error.message);
    }
  };

  const onFinish = (values) => {
    setMenuOpen(false);
    setVisibleMenuKey(null);
    const datesOptions = ['createdAt', 'dispatchedAt', 'updatedAt'];

    datesOptions.forEach((el) => {
      const dateValue = values[el];
      if (dateValue) {
        values[el] = FORMAT_DATE_TO_UTC(values[el]);
      }
    });

    if (values?.search) {
      values[searchType] = values?.search;
      delete values?.search;
    }
    if (values?.warehouses) {
      values['warehouses'] = values.warehouses;
    }

    handleOnSubmit(trimObjectStrings(cleanEmptyString(values)));
    setSelectedFilters(trimObjectStrings(cleanEmptyString(values)));
  };

  const handleResetFilters = () => {
    formRef.current.resetFields();
    handleOnSubmit({});
  };

  const handleValuesChange = useCallback(
    debounce(() => {
      formRef.current.submit();
    }, 500),
    []
  );

  const clearDateFilter = (key) => {
    formRef.current?.setFieldsValue({
      [key]: undefined
    });
    const updatedDateFilters = selectedDatesFilters;
    updatedDateFilters[key] = null;
    setSelectedDatesFilters(updatedDateFilters);
    formRef.current.submit();
  };

  const handleSelectChange = (value) => {
    setSearchType(value);
  };

  return (
    <Form
      onFinish={onFinish}
      onValuesChange={handleValuesChange}
      ref={formRef}
      initialValues={{
        ...(userWareHouse && { warehouses: userWareHouse }),
        dispatchedAt: dayjs()
      }}
    >
      <div className="br-routes-listing-filters-search__row">
        <span className="display-xs">
          {intl.formatMessage({ id: 'routes.routes_listing.page_title' })}
        </span>
        <div className="br-routes-search__right-side">
          <Form.Item name="search" className="br-routes-search__input-wrapper">
            <Input
              addonBefore={
                <div style={{ position: 'relative' }}>
                  <Select
                    dropdownClassName="br-routes-listing__search-options-dropdown"
                    className="br-routes-search__search-select"
                    onChange={handleSelectChange}
                    defaultValue={ROUTES_SEARCH_OPTIONS[0].value}
                    bordered={false}
                  >
                    {ROUTES_SEARCH_OPTIONS.map((el) => (
                      <Option key={el.value} value={el.value}>
                        {el.title}
                      </Option>
                    ))}
                  </Select>
                  <Down className="br-routes-search__custom-icon" />
                </div>
              }
              className="br-routes-search__search-input"
              placeholder={intl.formatMessage(
                { id: 'routes.routes_listing.search_for' },
                {
                  searchBy: ROUTES_SEARCH_OPTIONS.find(
                    (option) => option.value === searchType
                  ).title
                }
              )}
              allowClear
            />
          </Form.Item>
          <div className="br-routes-search__dropdown-searches">
            <div className="br-routes-search__hub-selector-wrapper">
              <Form.Item name="warehouses">
                <Select
                  onChange={(e) => handleHubChange(e)}
                  className="br-routes-search__hub-selector"
                  allowClear={!hubFilterDisabled}
                  showSearch
                  options={hubFilterDisabled ? scopedHubs : hubs}
                  disabled={
                    (hubFilterDisabled && scopedHubs?.length <= 1) || loading
                  }
                  optionFilterProp="name"
                  fieldNames={{ label: 'name', value: '_id' }}
                  placeholder={intl.formatMessage({
                    id: 'deliveries.lost_modal.hub_header'
                  })}
                  getPopupContainer={(trigger) => trigger.parentElement}
                />
              </Form.Item>
              <Down className="br-routes-search__hubs-custom-icon" />
            </div>
            <div className="br-routes-search__hub-selector-wrapper">
              <Form.Item name="starName">
                <Select
                  className="br-routes-search__hub-selector"
                  allowClear
                  showSearch
                  options={formatOptions(
                    '_id',
                    'profile.firstName,profile.lastName',
                    stars
                  )}
                  disabled={!stars?.length || loading}
                  fieldNames={{ label: 'label', value: 'label' }}
                  filterOption={(input, option) => {
                    const label = option?.label?.toString().toLowerCase() || '';
                    const value = option?.value?.toString().toLowerCase() || '';
                    return (
                      label.includes(input.toLowerCase()) ||
                      value.includes(input.toLowerCase())
                    );
                  }}
                  placeholder={intl.formatMessage({
                    id: 'deliveries.lost_modal.star_header'
                  })}
                  getPopupContainer={(trigger) => trigger.parentElement}
                />
              </Form.Item>
              <Down className="br-routes-search__hubs-custom-icon" />
            </div>
            <Dropdown
              overlay={menu}
              trigger={['click']}
              open={menuOpen}
              onOpenChange={(open) => {
                setMenuOpen(open);
              }}
            >
              <BRButton
                onClick={() => setVisibleMenuKey(null)}
                suffixIcon={<Filter />}
                label={intl.formatMessage({ id: 'common.filters' })}
                className="br-routes-search__filter-button"
              />
            </Dropdown>

            {items.map((el) => (
              <Form.Item
                key={el.key}
                name={el.key}
                className="br-routes-search__date-picker-hidden ant-form-item-hidden"
              >
                <DatePicker />
              </Form.Item>
            ))}

            {visibleMenuKey && (
              <div
                className="br-routes-search__date-picker-popup"
                onMouseDown={(e) => e.stopPropagation()}
                ref={rangePickerRef}
              >
                <Form.Item name={visibleMenuKey}>
                  <DatePicker
                    autoFocus
                    bordered={false}
                    inputReadOnly
                    open
                    onChange={(value) => {
                      if (value) {
                        const updatedDates = selectedDatesFilters;
                        updatedDates[visibleMenuKey] =
                          dayjs(value).format(DATE_FORMAT);
                        setSelectedDatesFilters(updatedDates);
                      }
                    }}
                  />
                </Form.Item>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="br-routes-search__selected-dates-filters">
        {items.map((el) => {
          return (
            selectedDatesFilters[el?.key] && (
              <div
                className="br-routes-search__single-date caption_md"
                key={el?.key}
              >
                <span className="br-routes-search-single-date__title">
                  {el?.title}
                  <span className="br-routes-search-single-date__value">
                    ({selectedDatesFilters[el?.key]})
                  </span>
                </span>
                <div
                  className="br-routes-search__close-icon"
                  onClick={() => clearDateFilter(el?.key)}
                >
                  <CloseOutlined />
                </div>
              </div>
            )
          );
        })}
      </div>
    </Form>
  );
};

export default RoutesSearch;
